import { useContext, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Context } from "../../context";
import { useState } from "react";
import { useCommonStyles } from "../../style_util";
import * as FC from "@fluentui/react-components";
import * as IC from "@fluentui/react-icons";
import { LabelValue } from "../../components/label_value";
import { TaskDef } from "../../services/task_service";
import {
    TaskPriorityBadge,
    TaskStatusBadge,
    enumTaskStatus,
    idString,
    mapTaskStatus,
    mapTaskTypeRecheck,
} from "../../db_utils";
import { toDateString, toDateTimeString } from "../../date_utils";
import { SimpleGrid } from "../../components/simple_grid";
import { TurbineDef } from "../../services/turbine_service";
import { ViewFrames } from "./view_frames";
import { useDrawer } from "../../components/use_drawer";

const getTask = async (apiClient, taskId) => {
    try {
        const task = await apiClient.getTask(taskId);
        return task;
    } catch (error) {
        console.error('Failed to load task:', error);
        return null;
    }
};

const normalizeStatus = (s) => {
    if (!s) return s;
    if (s === 'recheck') return 'rechecking';
    if (s === 'check') return 'checking';
    if (s === 'create') return 'created';
    return s;
};

const statusFromTurbine = (t) => {
    if (!t) return undefined;
    const s = t.annotation_status;
    if (s && s.length > 0) return normalizeStatus(s);
    // No explicit status: infer from readiness
    if (t.ready_for_annotation) return 'checking';
    return 'created';
};

const ViewTaskBase = ({ task, selectedTurbine, clearSelection }) => {
    const sessionStatus = task?.session?.status || task.status;
    const turbineStatusRaw = statusFromTurbine(selectedTurbine);
    const displayStatus = selectedTurbine ? turbineStatusRaw : normalizeStatus(sessionStatus);
    return (
        <div>
            <h3 className="text-base font-bold mb-4">基本信息</h3>
            <div className="grid grid-cols-[auto_1fr_auto_1fr_auto_1fr_auto_1fr] gap-x-8 gap-y-2 items-center">
                <LabelValue label="任务编号:" value={task.seq} />
                {TaskDef.label_value(task, "name")}
                {TaskDef.label_value(task, "field_item")}
                <LabelValue
                    label="巡检机数:"
                    value={task?.turbines?.length || ""}
                />
                <LabelValue
                    label="优先级:"
                    labelClassName={"justify-self-end"}
                    value={<TaskPriorityBadge priority={task.priority} />}
                />
                <LabelValue
                    label="复核方式:"
                    value={mapTaskTypeRecheck[task.type_recheck]?.text}
                />
                <LabelValue
                    label={selectedTurbine ? `风机状态(${selectedTurbine.seq})` : "当前状态:"}
                    value={<TaskStatusBadge status={displayStatus} />}
                />
                {/* todo */}
                <LabelValue
                    label="计划完成:"
                    value={toDateString(task.completed_at)}
                />
                {selectedTurbine && (
                    <div className="col-span-8">
                        <FC.Button size="small" appearance="subtle" onClick={clearSelection}>返回会话状态</FC.Button>
                    </div>
                )}
                <LabelValue
                    label="创建人员:"
                    value={task.owner_creater?.name}
                />
                <LabelValue
                    label="巡检人员:"
                    value={task.owner_checker?.name}
                />
                <LabelValue
                    label="复核人员:"
                    value={task.owner_rechecker?.name}
                    valueClassName={"col-span-3"}
                />
                {/* Hide description as it contains tool configuration JSON
                <LabelValue
                    label="备注说明:"
                    value={task.description}
                    valueClassName="col-span-3"
                />
                */}
            </div>
        </div>
    );
};

const ViewTaskProgress = ({ task, selectedTurbine }) => {
    const sessionStatus = normalizeStatus(task?.session?.status || task.status);
    const currentStatus = selectedTurbine
        ? statusFromTurbine(selectedTurbine)
        : sessionStatus;

    // Check review status for additional states
    const reviewStatus = task?.ReviewStatus || task?.reviewStatus;
    const isInRecheck = reviewStatus === "in_recheck";
    const isReadyToRelease = reviewStatus === "ready_to_release" || task?.IsReadyForRelease;
    const isReleased = reviewStatus === "released";

    // Determine current step including review states
    let step = enumTaskStatus.findIndex((it) => it.value == currentStatus);
    if (isInRecheck) step = 2.5; // Between annotation and rechecking
    if (isReadyToRelease) step = 5; // After completed, ready for release
    if (isReleased) step = 6; // Released state

    const doneColor = "#0078D4";
    const todoColor = "#EDEBE9";
    const recheckColor = "#FFA500"; // Orange for recheck
    const releaseColor = "#28A745"; // Green for release

    return (
        <div className="flex flex-col">
            <h3 className="text-base font-bold my-4">任务进度</h3>
            <div className="flex flex-row">
                <div
                    className="flex-1 border-b-2 py-4 text-center"
                    style={{
                        color: mapTaskStatus["created"]?.color,
                        borderColor: step >= 0 ? doneColor : todoColor,
                    }}
                >
                    1.新建
                </div>
                <div
                    className="flex-1 border-b-2 py-4 text-center"
                    style={{
                        color: mapTaskStatus["checking"]?.color,
                        borderColor: step >= 1 ? doneColor : todoColor,
                    }}
                >
                    2.巡检中
                </div>
                <div
                    className="flex-1 border-b-2 py-4 text-center"
                    style={{
                        color: isInRecheck ? recheckColor : mapTaskStatus["annotation"]?.color,
                        borderColor: step >= 2 ? (isInRecheck ? recheckColor : doneColor) : todoColor,
                    }}
                >
                    3.{isInRecheck ? "重新标注" : "标注中"}
                </div>
                <div
                    className="flex-1 border-b-2 py-4 text-center"
                    style={{
                        color: mapTaskStatus["rechecking"]?.color,
                        borderColor: step >= 3 ? doneColor : todoColor,
                    }}
                >
                    4.复核中
                </div>
                <div
                    className="flex-1 border-b-2 py-4 text-center"
                    style={{
                        color: mapTaskStatus["completed"]?.color,
                        borderColor: step >= 4 ? doneColor : todoColor,
                    }}
                >
                    5.完成
                </div>
                <div
                    className="flex-1 border-b-2 py-4 text-center"
                    style={{
                        color: isReadyToRelease ? releaseColor : "#999",
                        borderColor: step >= 5 ? releaseColor : todoColor,
                    }}
                >
                    6.待发布
                </div>
                <div
                    className="flex-1 border-b-2 py-4 text-center"
                    style={{
                        color: isReleased ? releaseColor : "#999",
                        borderColor: step >= 6 ? releaseColor : todoColor,
                    }}
                >
                    7.已发布
                </div>
            </div>
            <div className="flex flex-row text-xs pt-2">
                <div className="flex-1 text-center">
                    {step >= 0 && toDateTimeString(task.created_at)}
                </div>
                <div className="flex-1 text-center">
                    {step >= 1 && toDateTimeString(task.check_at)}
                </div>
                <div className="flex-1 text-center">
                    {step >= 2 && (isInRecheck ? toDateTimeString(task.RecheckRequestedAt) : toDateTimeString(task.annotation_at))}
                </div>
                <div className="flex-1 text-center">
                    {step >= 3 && toDateTimeString(task.recheck_at)}
                </div>
                <div className="flex-1 text-center">
                    {step >= 4 && toDateTimeString(task.completed_at)}
                </div>
                <div className="flex-1 text-center">
                    {step >= 5 && toDateTimeString(task.ReviewedAt)}
                </div>
                <div className="flex-1 text-center">
                    {step >= 6 && toDateTimeString(task.ReleasedAt)}
                </div>
            </div>
        </div>
    );
};

const ViewTaskStat = ({ task }) => {
    let labelClassName = "text-sm leading-6";
    let valueClassName = "h-10 text-lg font-bold";
    let wrapperProps = {
        className: "flex flex-col justify-center items-center",
    };
    let lvProps = {
        wrapper: "div",
        wrapperProps,
        labelClassName,
        valueClassName,
    };

    // Calculate review status counts
    const reviewStatus = task?.ReviewStatus || task?.reviewStatus;
    const isInRecheck = reviewStatus === "in_recheck";
    const isReadyToRelease = reviewStatus === "ready_to_release";
    const isReleased = reviewStatus === "released";

    // Calculate task status for the session
    const totalTasks = task?.turbines?.length || 0;
    const completedTasks = task?.turbines?.filter(t =>
        t.annotation_status === "completed" ||
        t.annotation_status === "recheck"
    ).length || 0;
    const inProgressTasks = totalTasks - completedTasks;

    return (
        <div>
            <h3 className="font-bold text-base my-4">数据分析</h3>
            <div className="flex flex-col gap-4">
                {/* Original statistics row */}
                <div className="flex flex-row flex-1 justify-around w-full">
                    <LabelValue
                        label="复核缺陷总数"
                        value={task.defection_count || 0}
                        {...lvProps}
                    />
                    <LabelValue
                        label="缺陷AI识别数"
                        value={task.ai_defection_count || 0}
                        {...lvProps}
                    />
                    <LabelValue
                        label="人工标识数"
                        value={task.human_defection_count || 0}
                        {...lvProps}
                    />
                    <LabelValue
                        label="提取图片数"
                        value={task.frame_count || 0}
                        {...lvProps}
                    />
                    <LabelValue
                        label="未复核图片"
                        value={task.unchecked_frame_count || 0}
                        {...lvProps}
                    />
                </div>

                {/* New review workflow statistics row */}
                <div className="flex flex-row flex-1 justify-around w-full border-t pt-4">
                    <LabelValue
                        label="风机总数"
                        value={totalTasks}
                        valueClassName={valueClassName + " text-blue-600"}
                        {...lvProps}
                    />
                    <LabelValue
                        label="已完成风机"
                        value={completedTasks}
                        valueClassName={valueClassName + " text-green-600"}
                        {...lvProps}
                    />
                    <LabelValue
                        label="进行中风机"
                        value={inProgressTasks}
                        valueClassName={valueClassName + " text-orange-600"}
                        {...lvProps}
                    />
                    <LabelValue
                        label="审核状态"
                        value={
                            <span style={{
                                padding: "4px 12px",
                                borderRadius: "4px",
                                fontSize: "14px",
                                fontWeight: "bold",
                                backgroundColor: isReleased ? "#d4edda" :
                                               isReadyToRelease ? "#cce5ff" :
                                               isInRecheck ? "#fff3cd" : "#f8f9fa",
                                color: isReleased ? "#155724" :
                                      isReadyToRelease ? "#004085" :
                                      isInRecheck ? "#856404" : "#6c757d"
                            }}>
                                {isReleased ? "已发布" :
                                 isReadyToRelease ? "待发布" :
                                 isInRecheck ? "重新标注" : "进行中"}
                            </span>
                        }
                        {...lvProps}
                    />
                    <LabelValue
                        label="完成进度"
                        value={
                            <div style={{ width: "100px" }}>
                                <div style={{
                                    height: "30px",
                                    backgroundColor: "#e9ecef",
                                    borderRadius: "4px",
                                    overflow: "hidden"
                                }}>
                                    <div style={{
                                        height: "100%",
                                        width: `${totalTasks > 0 ? (completedTasks / totalTasks * 100) : 0}%`,
                                        backgroundColor: completedTasks === totalTasks ? "#28a745" : "#17a2b8",
                                        transition: "width 0.3s ease",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "white",
                                        fontSize: "12px",
                                        fontWeight: "bold"
                                    }}>
                                        {totalTasks > 0 ? Math.round(completedTasks / totalTasks * 100) : 0}%
                                    </div>
                                </div>
                            </div>
                        }
                        {...lvProps}
                    />
                </div>
            </div>
        </div>
    );
};

const listTurbineOfTask = async (task) => {
    // Extract turbines from the task object
    const turbines = task?.turbines || [];
    console.log("turbinesOfTask:", turbines);
    return turbines;
};

const ViewTaskTurbineList = ({ task, selectedTurbine, setSelectedTurbine }) => {
    const { db, apiClient } = useContext(Context);
    const { drawer, openDrawer, closeDrawer } = useDrawer({
        position: "left",
        style: { width: 1000 },
    });
    const [items, setItems] = useState([]);
    const loadItems = async () => {
        let items = await listTurbineOfTask(task);
        setItems(items);
    };
    useEffect(() => {
        if (!task) {
            return;
        }
        loadItems();
    }, [task]);
    const columns = [
        FC.createTableColumn({
            columnId: "turbineSeq",
            renderHeaderCell: () => {
                return "风机编号";
            },
            renderCell: (item) => {
                return item.seq;
            },
        }),
        TurbineDef.createTableColumn("name"),
        FC.createTableColumn({
            columnId: "leafA",
            renderHeaderCell: () => {
                return <FC.TableCellLayout>叶片A</FC.TableCellLayout>;
            },
            renderCell: (item) => {
                return (
                    <FC.TableCellLayout>
                        {item.blades?.find((it) => it.attach_id == "A")?.seq || '-'}
                    </FC.TableCellLayout>
                );
            },
        }),
        FC.createTableColumn({
            columnId: "leafB",
            renderHeaderCell: () => {
                return <FC.TableCellLayout>叶片B</FC.TableCellLayout>;
            },
            renderCell: (item) => {
                return (
                    <FC.TableCellLayout>
                        {item.blades?.find((it) => it.attach_id == "B")?.seq || '-'}
                    </FC.TableCellLayout>
                );
            },
        }),
        FC.createTableColumn({
            columnId: "leafC",
            renderHeaderCell: () => {
                return <FC.TableCellLayout>叶片C</FC.TableCellLayout>;
            },
            renderCell: (item) => {
                return (
                    <FC.TableCellLayout>
                        {item.blades?.find((it) => it.attach_id == "C")?.seq || '-'}
                    </FC.TableCellLayout>
                );
            },
        }),
        // FC.createTableColumn({
        //     renderHeaderCell: () => {
        //         return <FC.TableCellLayout>状态</FC.TableCellLayout>;
        //     },
        //     renderCell: (item) => {
        //         return <FC.TableCellLayout>-</FC.TableCellLayout>;
        //     },
        // }),
        // FC.createTableColumn({
        //     renderHeaderCell: () => {
        //         return <FC.TableCellLayout>巡检时间</FC.TableCellLayout>;
        //     },
        //     renderCell: (item) => {
        //         return <FC.TableCellLayout>-</FC.TableCellLayout>;
        //     },
        // }),
        FC.createTableColumn({
            columnId: "ops",
            renderHeaderCell: () => {
                return <FC.TableCellLayout>操作</FC.TableCellLayout>;
            },
            renderCell: (item) => {
                const isCompleted = task?.status === "completed" || task?.status === "done";
                const onClick = () => {
                    if (!isCompleted) {
                        alert("只有在任务完成后才能查看数据");
                        return;
                    }
                    openDrawer({
                        header: "巡检数据",
                        body: (
                            <ViewFrames
                                taskId={task.id}
                                turbineId={item.id}
                                key={`${idString(task.id)}-${idString(
                                    item.id
                                )}`}
                            />
                        ),
                    });
                };
                return (
                    <FC.TableCellLayout>
                        <FC.Link appearance="subtle" onClick={() => setSelectedTurbine?.(item)} className="mr-3">选择</FC.Link>
                        {isCompleted ? (
                            <FC.Link onClick={onClick}>查看数据</FC.Link>
                        ) : (
                            <FC.Text style={{ color: '#999', cursor: 'not-allowed' }}>
                                查看数据（任务未完成）
                            </FC.Text>
                        )}
                    </FC.TableCellLayout>
                );
            },
        }),
    ];
    return (
        <div className="flex flex-col flex-1">
            <h3 className="text-base font-bold my-4">风机列表</h3>
            <SimpleGrid
                items={items}
                columns={columns}
                className="flex-1"
                getRowId={(item) => idString(item.id)}
                onRowClick={(item) => setSelectedTurbine?.(item)}
            />
            {drawer}
        </div>
    );
};

export const DatacenterViewTaskManagement = () => {
    const { taskId } = useParams();
    const { db, apiClient } = useContext(Context);
    const [task, setTask] = useState(null);
    const [turbine, setTurbine] = useState(null);
    const nav = useNavigate();
    const loadTask = async () => {
        let task = await getTask(apiClient, taskId);
        setTask(task);
    };
    useEffect(() => {
        if (!apiClient) {
            return;
        }
        loadTask();
    }, [apiClient, taskId]);

    const commonStyles = useCommonStyles();
    return (
        <div className={commonStyles.root}>
            <div>
                <FC.Button
                    appearance="subtle"
                    onClick={() => {
                        nav(-1);
                    }}
                    icon={<IC.ArrowHookDownLeftFilled />}
                >
                    返回列表
                </FC.Button>
            </div>

            {task && (
                <ViewTaskBase
                    task={task}
                    selectedTurbine={turbine}
                    clearSelection={() => setTurbine(null)}
                />
            )}
            {task && <ViewTaskProgress task={task} selectedTurbine={turbine} />}
            {task && <ViewTaskStat task={task} />}
            {task && (
                <ViewTaskTurbineList
                    task={task}
                    selectedTurbine={turbine}
                    setSelectedTurbine={setTurbine}
                />
            )}
        </div>
    );
};
